import { useState } from 'react';
import NotesMode from '../NotesMode/NotesMode';
import './StageDetail.css';

function getStatusLabel(status) {
  if (status === 'COMPLETE') return '✓ Complete';
  if (status === 'IN_PROGRESS') return '● In Progress';
  if (status === 'PENDING_REVIEW') return '⏳ Pending Review';
  return '○ Not Started';
}

function getStatusClass(status) {
  if (status === 'COMPLETE') return 'cj-sd__status--complete';
  if (status === 'IN_PROGRESS') return 'cj-sd__status--progress';
  if (status === 'PENDING_REVIEW') return 'cj-sd__status--review';
  return 'cj-sd__status--not-started';
}

export default function StageDetail({
  stage,
  onToggleItem,
  onSubmitComplete,
  onApprove,
  onReject,
  onAddNote,
  notes,
  currentUserRole,
  saving,
  gateError,
  onClearGateError,
  users
}) {
  const [rejectComment, setRejectComment] = useState('');
  const [showRejectInput, setShowRejectInput] = useState(false);

  const { stageUid, stageName, stageIcon, stagePercent, status, checklist, reviewComment } = stage;

  const isRM = currentUserRole === 'RM' || currentUserRole === 'Administrator';
  const isPlanner = currentUserRole === 'Financial Planner' || currentUserRole === 'Supervisor' || currentUserRole === 'Administrator';
  const isLocked = status === 'COMPLETE' || status === 'PENDING_REVIEW';

  const doneCount = checklist ? checklist.filter(i => i.status === 'DONE').length : 0;
  const totalCount = checklist ? checklist.length : 0;

  function handleCheckboxClick(e, item) {
    e.stopPropagation();
    if (isLocked || item.itemType === 'DOCUMENT') return;
    onToggleItem(stageUid, item.itemUid);
  }

  function handleRejectClick() {
    setShowRejectInput(true);
  }

  function handleRejectConfirm() {
    onReject(stageUid, rejectComment);
    setShowRejectInput(false);
    setRejectComment('');
  }

  function handleRejectCancel() {
    setShowRejectInput(false);
    setRejectComment('');
  }

  return (
    <div className="cj-sd">
      {/* Stage header */}
      <div className="cj-sd__header">
        <div className="cj-sd__header-top">
          <span className="cj-sd__stage-icon">{stageIcon}</span>
          <h3 className="cj-sd__stage-name">{stageName}</h3>
          <span className={`cj-sd__status ${getStatusClass(status)}`}>
            {getStatusLabel(status)}
          </span>
        </div>
        <div className="cj-sd__progress-row">
          <div className="cj-sd__progress-bar">
            <div className="cj-sd__progress-fill" style={{ width: `${stagePercent || 0}%` }} />
          </div>
          <span className="cj-sd__progress-label">{doneCount}/{totalCount} items · {stagePercent || 0}%</span>
        </div>
      </div>

      {/* Gate error */}
      {gateError && (
        <div className="cj-sd__gate-error">
          <span>⚠ {gateError}</span>
          <button className="cj-sd__gate-error-close" onClick={onClearGateError}>✕</button>
        </div>
      )}

      {/* Planner rejection note */}
      {reviewComment && status === 'IN_PROGRESS' && (
        <div className="cj-sd__rejection-note">
          <span className="cj-sd__rejection-label">Planner note:</span> {reviewComment}
        </div>
      )}

      {/* Checklist */}
      <div className="cj-sd__section">
        <div className="cj-sd__section-title">Checklist</div>
        {checklist && checklist.length > 0 ? (
          <ul className="cj-sd__checklist">
            {checklist.map(item => (
              <li
                key={item.itemUid}
                className={`cj-sd__item ${item.status === 'DONE' ? 'cj-sd__item--done' : ''} ${item.status === 'PENDING_REVIEW' ? 'cj-sd__item--review' : ''}`}
              >
                <button
                  className={`cj-sd__checkbox ${item.status === 'DONE' ? 'cj-sd__checkbox--done' : ''} ${(isLocked || item.itemType === 'DOCUMENT') ? 'cj-sd__checkbox--locked' : ''}`}
                  onClick={(e) => handleCheckboxClick(e, item)}
                  disabled={isLocked || item.itemType === 'DOCUMENT'}
                >
                  {item.status === 'DONE' ? '✓' : ''}
                </button>
                <div className="cj-sd__item-content">
                  <span className="cj-sd__item-text">{item.text}</span>
                  {item.hint && <span className="cj-sd__item-hint">{item.hint}</span>}
                  {item.rejectionNote && (
                    <span className="cj-sd__item-rejection">Rejected: {item.rejectionNote}</span>
                  )}
                </div>
                <div className="cj-sd__item-right">
                  {item.required && item.status !== 'DONE' && (
                    <span className="cj-sd__item-required">required</span>
                  )}
                  {item.status === 'PENDING_REVIEW' && (
                    <span className="cj-sd__item-badge cj-sd__item-badge--review">Review</span>
                  )}
                  {item.itemType === 'DOCUMENT' && (
                    <span className="cj-sd__item-doc">📎</span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="cj-sd__empty">No checklist items for this stage.</p>
        )}
      </div>

      {/* Actions */}
      <div className="cj-sd__actions">
        {(status === 'NOT_STARTED' || status === 'IN_PROGRESS') && isRM && (
          <button
            className="cj-sd__btn cj-sd__btn--submit"
            onClick={() => onSubmitComplete(stageUid)}
            disabled={saving}
          >
            {saving ? 'Submitting...' : 'Mark Complete'}
          </button>
        )}
        {status === 'PENDING_REVIEW' && isRM && (
          <span className="cj-sd__submitted-label">⏳ Submitted for review</span>
        )}
        {status === 'PENDING_REVIEW' && isPlanner && !showRejectInput && (
          <div className="cj-sd__planner-actions">
            <button className="cj-sd__btn cj-sd__btn--approve" onClick={() => onApprove(stageUid)} disabled={saving}>
              ✓ Approve
            </button>
            <button className="cj-sd__btn cj-sd__btn--reject" onClick={handleRejectClick} disabled={saving}>
              ✗ Reject
            </button>
          </div>
        )}
        {showRejectInput && (
          <div className="cj-sd__reject-form">
            <input
              className="cj-sd__reject-input"
              placeholder="Rejection reason (optional)..."
              value={rejectComment}
              onChange={e => setRejectComment(e.target.value)}
            />
            <div className="cj-sd__reject-btns">
              <button className="cj-sd__btn cj-sd__btn--reject" onClick={handleRejectConfirm}>Confirm Reject</button>
              <button className="cj-sd__btn cj-sd__btn--cancel" onClick={handleRejectCancel}>Cancel</button>
            </div>
          </div>
        )}
        {status === 'COMPLETE' && (
          <span className="cj-sd__complete-label">✓ Completed</span>
        )}
      </div>

      {/* Notes */}
      <div className="cj-sd__section cj-sd__section--notes">
        <div className="cj-sd__section-title">Notes</div>
        <NotesMode
          notes={notes}
          onAddNote={onAddNote}
          saving={saving}
          users={users}
        />
      </div>
    </div>
  );
}
